import { useRouter } from 'next/router';
import { usePathname, useSearchParams } from 'next/navigation';
import { useDebouncedCallback } from 'use-debounce';

export type SeveralFilters = {
	label: string;
	value: string | undefined;
};

export const useFilters = () => {
	const pathname = usePathname();
	const router = useRouter();
	const { replace, query } = router;
	const searchParams = useSearchParams();
	const params = new URLSearchParams(searchParams);

	const setParamFilter = (
		label: string,
		value: string | undefined,
		page = '1'
	) => {
		if (value) {
			params.set(label, value);
		} else {
			params.delete(label);
		}

		replaceUrl(page);
	};

	const setParamSeveralFilters = (values: SeveralFilters[]) => {
		values.forEach((item) => {
			if (item.value) {
				params.set(item.label, item.value);
			} else {
				params.delete(item.label);
			}
		});
		replaceUrl('1');
	};

	const searchFilter = useDebouncedCallback(
		(value: string | undefined, key?: string): void => {
			const paramKey = key ?? 'search';
			if (value) {
				params.set(paramKey, value);
			} else {
				params.delete(paramKey);
			}
			replaceUrl('1');
		},
		300
	);

	const removeParaFilter = (key: string) => {
		params.delete(key);
		replaceUrl('1');
	};

	const replaceUrl = (page: string) => {
		params.set('page', page);
		replace(`${pathname}?${params.toString()}`);
	};

	const hasFilter = (value: string) => {
		return !!query[value];
	};

	const lastFilter = (dismissKeys: string[] = []) => {
		const ignoredKeys = new Set(['page', ...dismissKeys]);

		// Create a reverse array of query params
		const reversedParams = Array.from(searchParams.entries()).reverse();

		const last = reversedParams.find(([key, value]) => {
			return !ignoredKeys.has(key) && value !== undefined && value !== '';
		});

		return last ? last[0] : '';
	};

	const clearAllFilters = () => {
		Array.from(params.keys()).forEach((key) => {
			params.delete(key);
		});
		replaceUrl('1');
	};

	const clearSpecificFilters = (keys: string[]) => {
		keys.forEach((key) => {
			params.delete(key);
		});
		replaceUrl('1');
	};

	return {
		hasFilter,
		lastFilter,
		removeParaFilter,
		replaceUrl,
		setParamFilter,
		setParamSeveralFilters,
		clearAllFilters,
		clearSpecificFilters,
		searchFilter,
		params,
		query,
	};
};
